import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AddUserModal = ({ isOpen, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    role: 'coordinator',
    department: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validate email
      if (!formData?.email || !formData?.email?.includes('@')) {
        throw new Error('Please enter a valid email address');
      }

      // Validate full name
      if (!formData?.fullName || formData?.fullName?.trim()?.length < 2) {
        throw new Error('Please enter a valid full name');
      }

      await onAdd(formData);
      handleClose();
    } catch (err) {
      setError(err?.message || 'Failed to add user');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      email: '',
      fullName: '',
      role: 'coordinator',
      department: '',
      phone: ''
    });
    setError('');
    onClose();
  };

  const roleOptions = [
    { value: 'coordinator', label: 'Coordinator - Entry Only' },
    { value: 'supervisor', label: 'Supervisor - Entry + Team Reports' },
    { value: 'manager', label: 'Manager - Full Reports + User Management' },
    { value: 'administrator', label: 'Administrator - System Configuration' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-16">
      <div className="fixed inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative bg-card rounded-lg shadow-elevation-3 border border-border w-full max-w-[500px] max-h-[90vh] overflow-y-auto">
        <div className="p-24 md:p-32 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-12">
              <div className="w-[40px] h-[40px] bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="UserPlus" size={20} color="var(--color-primary)" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold text-foreground">Add New User</h2>
                <p className="text-sm text-muted-foreground">Create a new team member account</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-24 md:p-32 space-y-20">
            {error && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-12 flex items-start gap-12">
                <Icon name="AlertCircle" size={16} color="var(--color-error)" className="mt-2" />
                <p className="text-sm text-error flex-1">{error}</p>
              </div>
            )}

            <Input
              label="Email Address"
              type="email"
              required
              value={formData?.email}
              onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
              placeholder="user@example.com"
              description="User will receive an invitation email"
            />

            <Input
              label="Full Name"
              required
              value={formData?.fullName}
              onChange={(e) => setFormData({ ...formData, fullName: e?.target?.value })}
              placeholder="John Smith"
            />

            <Select
              label="Role"
              required
              options={roleOptions}
              value={formData?.role}
              onChange={(value) => setFormData({ ...formData, role: value })}
              description="Determines user permissions and access levels"
            />

            <Input
              label="Department"
              value={formData?.department}
              onChange={(e) => setFormData({ ...formData, department: e?.target?.value })}
              placeholder="Operations"
            />

            <Input
              label="Phone Number"
              type="tel"
              value={formData?.phone}
              onChange={(e) => setFormData({ ...formData, phone: e?.target?.value })}
              placeholder="+44 20 7946 0958"
            />

            <div className="bg-primary/5 border border-primary/20 rounded-lg p-16">
              <div className="flex items-start gap-12">
                <Icon name="Info" size={16} color="var(--color-primary)" className="mt-2" />
                <div className="flex-1">
                  <p className="text-sm text-foreground font-medium mb-4">Initial Password</p>
                  <p className="text-xs text-muted-foreground">
                    A temporary password will be generated and sent to the user via email. They will
                    be required to change it on first login.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-24 md:p-32 border-t border-border flex justify-end gap-12">
            <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" loading={loading} iconName="UserPlus">
              Add User
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserModal;